import Ajv from 'ajv';
import { saveAs } from 'file-saver';
import * as Papa from 'papaparse';
import _ from 'lodash';
const dotProp = require('dot-prop');
import {
    channelNameToType,
    channelTypeToName
} from '../const';
import {
    AvsFileName,
    Design,
    DesignChannelName,
    NodeType
} from '../types';

import ExportManager from './ExportManager';
import Utility from './Utility';
import { Constant } from '../const';
import ObjectsUsedIn from './usedIn/ObjectsUsedIn';
import nodeTypeMap from './NodeTypeMap';
import { getDsids } from './functions';

/**
 * @module Exporter
 * JSON file exporter
 */
export default class Exporter {
    static createJsonBlob(data) {
        return new Blob([JSON.stringify(data)], { type: "application/json" });
    }

    /**
     * @param {Design} serialized
     */
    static getFileName({
        branchName,
        clientName,
        designName,
        version
    }) {
        return [
            clientName,
            designName,
            branchName,
            version
        ].join('-');
    }

    /**
     * Get node type mapping
     * @param className
     * @return {*}
     */
    static getType(className) {
        return nodeTypeMap[className];
    }

    /**
     * Transform and export properties
     * @param props
     */
    static toCsv(arr) {
        return arr.reduce(function (csvString, row) {
            csvString += row.join(',');
            csvString += "\r\n";  //";";//"\n";
            return csvString;
        }, '');
    }
    static exportProps(props) {
        const newProps = JSON.parse(JSON.stringify(props));
        delete newProps.updatedTime;
        delete newProps.nodeTypeName;
        delete newProps.colorClass;
        delete newProps.iconClass;
        delete newProps.error;
        delete newProps.configView;
        delete newProps.permission;
        delete newProps.usedIn;
        delete newProps.version;
        delete newProps.config.version;
        delete newProps.isRootModule;
        delete newProps.moduleApp;
        delete newProps.updatedTime;
        delete newProps.iconClass;
        delete newProps.readOnly;
        delete newProps.lastLocalChange;
        delete newProps.lastSavedChange;
        delete newProps.createdTime;
        delete newProps.timeAddedToFlow;
        delete newProps.timeAddedtoFlow;
        return newProps;
    }

    /**
     * Normalize input data according to DEVDES2-902
     * @param variable
     */
    static normalizeInput(input) {
        if (input.type && input.type.toLowerCase() === "value") {
            input.varName = input.varName ? input.varName : null;
        }
    }

    /**
     * Remove TimeStamps for CompoundIntents && GlobalIntents
     * @param {} data
     */
    static deleteTimeStamps(data) {
        data.forEach(d => {
            delete d.lastLocalChange;
            delete d.lastSavedChange;
            delete d.createdTime;
        });
        return data;
    }

    /**
     * remove non-essential properties from exported globalIntent objects
     * move inputs as direct property of ModuleInputs
     * @param {array} globalIntents
     */
    static getGlobalHandling(globalIntents){
        // moduleProperties
        globalIntents.forEach(gi => {
            if(gi.moduleProperties){
                const inputs = _.cloneDeep(gi.moduleProperties.props.config.inputs);

                dotProp.delete(gi.moduleProperties, 'id');
                dotProp.delete(gi.moduleProperties, 'name');
                dotProp.delete(gi.moduleProperties, 'type');
                dotProp.delete(gi.moduleProperties, '_class');
                dotProp.delete(gi.moduleProperties, 'props');

                gi.moduleProperties.inputs = inputs;
            }
        });

        return globalIntents;
    }

    /**
    * Remove all disabledInFlow modules from serialized.components.modules
    * @param {json} serialized
    * @return {json} modified serialized
    */
    static deleteDisabledModules(serialized) {
        const components = serialized.components;
        components.modules.map(mod => {
            components.modules = components.modules.filter(mod => !mod.props.config.disabledInFlow);
        });

        return serialized;
    }

    /**
     * Remove all disabledInFlow items from serialized.components.modules
     * @param {json} serialized
     * @return {json} modified serialized
     */
    static deletedDisabledInFlowItems(serialized) {
        const mods = serialized.components.modules;

        mods.map(mod => {
            const flow = mod.versions[1].flow;
            flow.links = flow.links.filter(l => !l.props.config.disabledInFlow);
            flow.nodes = flow.nodes.filter(n => !n.props.config.disabledInFlow);
        });

        return serialized;
    }
    /**
     * change the variable typr PROMPT and MODULE to STRING for all outputs
     * @param {json} config
     * @return {json} modified serialized
     */
    static getOutPuts(node) {
        if (node.props.config.outputs && node.props.config.outputs.length > 0) {
            node.props.config.outputs.forEach(output => {
                // DEVDES2-1366: type should be uppercase
                // https://git.interactions.net/ps-tooling/d2-export-schema/blob/develop/d2-export-schema-json/src/main/resources/json/subSchemas/variableType.schema.json
                output.type = output.type.toUpperCase();
                if (output.type && (output.type === "PROMPT" || output.type === "MODULE")) {
                    output.type = "STRING";
                }
                if (node.type === "TABULAR") {
                    var outPutIndex = node.props.config.outputTo.findIndex(ou => ou.varName === output.varName);
                    if (outPutIndex !== -1) {
                        output.argumentName = node.props.config.outputTo[outPutIndex].argumentName;
                        output.sensitiveData = node.props.config.outputTo[outPutIndex].sensitiveData;
                    }
                }
            });
            return node.props.config.outputs;
        }
        else return [];
    }
    /**
     * rename reusbale functions
     * @param {Design} serialized

     */
    static renameReusableFunction(serialized) {
        if (!serialized.functions) {
            return "";
        }

        const appName = serialized.repoApplication.replace(/ /g, "").toLowerCase();
        const functions = _.cloneDeep(serialized.functions);
        const functionsNames = new Map(functions.map(({ name }) => {
            return [name, `${appName}$${name.toLowerCase()}`];
        }));
        return functions.map((fn) => {
            let parameters = (fn.parameters ?? []).map(({ key }) => {
                return key;
            }).join(", ");
            functionsNames.forEach((replaceName, name) => {
                const nameSearch = new RegExp(`${name}(?=\\()` , "g");
                fn.script = fn.script?.replace(nameSearch, replaceName);
            });

            return [
                `function ${functionsNames.get(fn.name)}(${parameters}) {`,
                `  /* ID:${fn.id}*/`,
                `  ${fn.script}`,
                "}"
            ].join("\n");
        }).join("\n");
    }

    /**
     * Export designer2-format JSON.
     * @param {Design} serialized
     */
    static exportDesignerFile(serialized) {
        serialized.components.objects.filter(obj => obj._class === NodeType.prompt).forEach(prompt => {
            // DEVDES2-965: Add "unused" key to prompts in module flows
            // exclude disabledInFlow and sample dialog prompts
            const promptUsedIn = ObjectsUsedIn.getObjectUsedIn(prompt.id, serialized, {
                'excludeDisabledInFlow': true,
                'excludeSampleDialogs': true
            });
            // updateNodePropsUnused is in ExportManager
            Utility.updateNodePropsUnused(prompt, promptUsedIn.filter(arr => arr.length > 0).length === 0);
            if (prompt.localVariables) {
                delete prompt.localVariables;
            }
        });

        const exportObj = JSON.parse(JSON.stringify(serialized));
        delete exportObj["$loki"];
        delete exportObj["meta"];
        delete exportObj["tabs"];
        saveAs(this.createJsonBlob(exportObj), `${this.getFileName(serialized)}.json`);
    }

    /**
     * Export engine-format JSON.
     * @param {Design} serialized
     */
    static exportEngineFile(serialized) {
        const fileName = this.getFileName(serialized);
        const exportManager = new ExportManager();
        const functionObj = Exporter.renameReusableFunction(serialized);
        const { isOmniChannel } = serialized.omniChannel;

        // clean up serialized
        Utility.updatePromptsUsedIn(serialized);
        Utility.removeDuplicateComponents(serialized);

        // do not modify serialized when passing to ExportManager, instead pass in a copy of serialized
        const copyObj = _.cloneDeep(serialized)
        const exportObj = exportManager.getExport(copyObj);
        const designObj = JSON.parse(JSON.stringify(serialized));

        delete designObj["$loki"];
        delete designObj["meta"];
        delete designObj["tabs"];
        delete designObj["unitTests"];

        var JSZip = require("jszip");
        var zip = new JSZip();
        var exportFile = zip.folder(fileName, { base64: true });

        // get prompts data for Prompt.csv export
        const promptObjs = serialized.components.objects.filter(obj => obj._class === "PromptNodeModel");
        const languages = serialized.languages.map((l) => l.language)
        // header fields for papa parse/unparse
        // https://www.papaparse.com/docs#json-to-csv
        // Use imported Prompts.csv header fields, or compose fields with language fields on serialized
        const localeExportFields =
          serialized.localeExportFields ||
          ["Name", "Workflow Tool Name",'Channel Layer'].concat(languages);

        const getRowData = (
          prompt,
          channelName
        ) => {
          const { channel, text } = prompt.props.config ?? {};
          const promptObjs = channel?.[channelNameToType[channelName]] ?? text;
          if (!promptObjs) {
            return [];
          }

          let row = [];

          row.push(prompt.name);
          row.push(prompt.wftName || "");
          row.push(channelName ?? DesignChannelName.voice);
          languages.forEach((lang) => {
            row.push(promptObjs[lang] || "");
          });
          return row;
        };

        if (isOmniChannel) {
          const channelNames = Object.keys(
            serialized.rootSettings.channels
          ).map((type) => {
            return channelTypeToName[type];
          });
          for (const channelName of channelNames) {
            const promptsRowData = promptObjs.reduce(
              (acc, prompt) => {
                const row = getRowData(prompt, channelName);
                if (row.length > 0) {
                  return [...acc, row];
                }
                return acc;
              },
              [localeExportFields]
            );

            if (promptsRowData.length > 1) {
              const newCsv = Papa.unparse(promptsRowData);
              exportFile.file(`Prompts_${channelName}.csv`, newCsv);
            }
          }
        } else {
          const promptsRowData = [
            localeExportFields,
            ...promptObjs.map((prompt) => getRowData(prompt)),
          ];
          const newCsv = Papa.unparse(promptsRowData);
          exportFile.file(AvsFileName.prompts, newCsv);
        }

        let inputCsvHeaderFields = ['InputName', 'InputType', 'SamplePrompt', 'SamplePromptText', 'ModuleName', 'Sensitive', 'DTMF'];
        let inputsRowData = [inputCsvHeaderFields];
        let inputObjs = [];
        var Inputs = [];
        window.apm.serialized.components.modules.forEach(module => {
            module.versions[1].flow.nodes.forEach(nod => {
                if (nod._class === "InputNodeModel") {
                    let promObjs = [];
                    for (var li = 0; li < module.versions[1].flow.links.length; li++) {
                        var link = module.versions[1].flow.links[li];
                        if (link.target === nod.id) {
                            var prIndex = module.versions[1].flow.nodes.findIndex(el => el.id === link.source);
                            if (module.versions[1].flow.nodes[prIndex]._class === "PromptNodeModel") {
                                promObjs.push(module.versions[1].flow.nodes[prIndex]);
                            }
                        }
                    }
                    var inputObjIndex = inputObjs.findIndex(object => object.input.name === nod.name);
                    if (inputObjIndex === -1) { inputObjs.push({ input: nod, prompt: promObjs[0], module: module.name }); }
                    Inputs.push(nod.props.parentComponentID);
                }
            });
        });
        var inputs = _.filter(window.apm.serialized.components.objects, { _class: "InputNodeModel" });
        inputs.forEach(input => {
            var inputIndex = Inputs.findIndex(inp => inp === input.id);
            if (inputIndex === -1) {
                inputObjs.push(({ input: input, prompt: undefined, module: undefined }));
            }
        });
        inputObjs.map(obj => {
            let row = [];
            row.push(obj.input.name);
            row.push(obj.input.props.config.inputType);

            if (obj.prompt) {
                row.push(obj.prompt.name);
                row.push(obj.prompt.props.config.text["en-US"]);
                row.push(obj.module);
            }
            else {
                row.push("Unused","","");
            }

            if(obj.input.props.config.sensitiveData) {
                row.push(obj.input.props.config.sensitiveData);
            } else {
                row.push('FALSE');
            }
            if(obj.input.props.config.DTMF) {
                row.push(obj.input.props.config.DTMF);
            } else {
                row.push('FALSE');
            }
            inputsRowData.push(row);
        });
        const newInputCsv = Papa.unparse(inputsRowData);
        const properties = window.apm.serialized.appProperties.filter(p => p.type === "variableBinding");
        let propStr = "";
        if (properties) {
            properties.forEach(p => {
                const variable = window.apm.getVariable(p.variable.split(".")[1]);
                if (variable) {
                    switch (variable.props.config.type) {
                    case "STRING":
                        propStr += `${p.config}=${p.default ? "\"" +p.default + "\"" : ""}\n`;
                        break;
                    case "BOOLEAN":
                        propStr += `${p.config}=${p.default === "false" ? false : !!p.default}\n`;
                        break;
                    default:
                        propStr += `${p.config}=${p.default ? p.default : ""}\n`;
                    }
                }
            });
        }
        const propFile = new Blob([propStr], { type: "text/plain;charset=utf-8" });
        exportFile.file(AvsFileName.inputs, newInputCsv);
        exportFile.file(AvsFileName.export, this.createJsonBlob(exportObj));
        exportFile.file(AvsFileName.design, this.createJsonBlob(designObj));
        exportFile.file(AvsFileName.dsids, this.createJsonBlob(getDsids(serialized)));
        exportFile.file(AvsFileName.appCcxml, copyObj.ccxml);
        exportFile.file(`${window.apm.serialized.client}_${window.apm.serialized.client}${window.apm.serialized.application}.properties`.toLowerCase(), propFile);
        exportFile.file(AvsFileName.unitTests, this.createJsonBlob(serialized.unitTests));

        if (functionObj.length > 0) {
            exportFile.file(AvsFileName.functions, functionObj);
        }

        if (exportObj.csvData.length > 0) {
            for (var cf = 0; cf < exportObj.csvData.length; cf++) {
                exportFile.file(designObj.csvData[cf].name + ".csv", designObj.csvData[cf].value);
            }
        }

        zip.generateAsync({
            type: "blob",
            compression: "DEFLATE",
            compressionOptions: {
                level: 5
            }
        }).then(function (content) {
            saveAs(content, `${fileName}.zip`);
        });
    }

    /**
     * Fetch and return a specific version of D2 export JSON schema.
     * Currently using the version defined on `EXPORT_SCHEMA_VERSION` on server.js
     * @returns {object} The export JSON schema
     */
    static async getExportSchemaJson() {
        try {
            let response = await fetch('/export-schema');
            response = await response.json();
            return response.schema;
        } catch (e) {
            throw e;
        }
    }

    static async getExportSchemaValidationErrors(serialized) {
        try {
            // Clean up serialized.
            Utility.updatePromptsUsedIn(serialized);
            Utility.removeDuplicateComponents(serialized);

            // Don't modify serialized when passing it to ExportManager (create a copy instead).
            const exportManager = new ExportManager();
            const serializedCopy = _.cloneDeep(serialized);
            const exportObject = exportManager.getExport(serializedCopy);

            // First try to fetch schema JSON from cache.
            let schemaJson = window.apm.cache.get(
                Constant.k.NAMESPACE_CLIENT,
                'export-schema-json',
            );

            // Fetch the export schema JSON from API if not in cache.
            if (!schemaJson) {
                schemaJson = await Exporter.getExportSchemaJson();

                // Cache the schema so we don't request it again if already available.
                // It won't likely change unless we fetch a different version (see server.js).
                window.apm.cache.put(
                    Constant.k.NAMESPACE_CLIENT,
                    'export-schema-json',
                    schemaJson,
                );
            }

            // Compile and validate the passed in serialized against the schema.
            const errors = [];
            const ajv = new Ajv({ allErrors: true });
            const validateFn = ajv.compile(schemaJson);
            const isValid = validateFn(exportObject);

            // Format schema errors.
            if (!isValid) {
                validateFn.errors.forEach((e) => {
                    // dataPath is a string like this '.components[157].flow.nodes[2]'
                    // so we remove the first character in order to get the component/node.
                    const node = _.get(exportObject, e.dataPath.slice(1, -1));
                    errors.push({
                        componentName: node?.name || null,
                        message: e.message || 'has validation errors against export schema',
                        keyword: e.keyword,
                        dataPath: e.dataPath,
                        schemaPath: e.schemaPath,
                        params: e.params || {},
                    });
                });
            }

            return errors;
        } catch {
            // If for some reason the export schema JSON cannot be fetched
            // we assume that there aren't errors because we won't be able to
            // compile nor compare the current serialized object.
            return [];
        }
    }
}
